import type en from '@/messages/en.json'
import { directionFor } from './direction'

/**
 * The interface languages: exactly the catalogues in messages/. The catalogue
 * guard (__tests__/catalogue-integrity.guard.spec.ts) fails if this list, the
 * labels, the loaders and the files on disk ever disagree.
 */
export const SUPPORTED_LOCALES = ['en', 'ar', 'fr', 'hi'] as const

export type Locale = (typeof SUPPORTED_LOCALES)[number]

export type Messages = typeof en

export interface I18nContextValue {
  locale: Locale
  /** Switch the interface language. A value without a catalogue is ignored, never saved. */
  setLocale: (locale: Locale) => void
  t: (key: string, params?: Record<string, string | number>) => string
}

export const LOCALE_STORAGE_KEY = 'hf-locale'

/** The cookie that mirrors the stored choice, so the server can read it too. */
export const LOCALE_COOKIE_NAME = 'hf-locale'

export const RTL_LOCALES: Locale[] = SUPPORTED_LOCALES.filter((l) => directionFor(l) === 'rtl')

/** Each language in its own name and script. */
export const LOCALE_LABELS: Record<Locale, string> = {
  en: 'English',
  ar: 'العربية',
  fr: 'Français',
  hi: 'हिन्दी',
}

/** Each language's English name: what an admin types to find it in a picker. */
export const LOCALE_ENGLISH_NAMES: Record<Locale, string> = {
  en: 'English',
  ar: 'Arabic',
  fr: 'French',
  hi: 'Hindi',
}

/**
 * Options for any picker that sets the INTERFACE language (the switcher, a
 * workspace or personal language default). Not LANGUAGE_OPTIONS: that 24-entry
 * list is for content (a Content Studio deck can be generated in Spanish), and
 * only these four have a catalogue.
 *
 * `label` is the language in its own script (the display); `englishName` lets
 * a SearchableSelect find "العربية" when the admin types "Arabic": pass
 * `searchKeys={UI_LOCALE_SEARCH_KEYS}`.
 */
export const UI_LOCALE_OPTIONS: ReadonlyArray<{ value: Locale; label: string; englishName: string }> =
  SUPPORTED_LOCALES.map((value) => ({ value, label: LOCALE_LABELS[value], englishName: LOCALE_ENGLISH_NAMES[value] }))

/** The SearchableSelect `searchKeys` for UI_LOCALE_OPTIONS. */
export const UI_LOCALE_SEARCH_KEYS: string[] = ['englishName']
